import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import api from "../../utils/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Combobox } from "../../components/ui/combobox";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Users,
  FileText,
  Mail,
  Info,
  Monitor,
  Smartphone,
  Shield,
  Wrench,
  Plus,
  Trash2,
  ChevronDown,
  CheckCircle,
  X,
} from "lucide-react";
import { RequirementCategory, RequirementItem, LEAD_OPTIONS, mapRequirementCategoryFromApi } from "../../types/onboarding-requirements";

// ===================================================================
// Interfaces
// ===================================================================
interface NotificationSubscriber {
  id: number;
  name: string;
  email: string;
  initials: string;
  avatarColor: string;
}

// One user added to a notification section; `enabled` is the tick that
// controls whether they actually receive the email.
interface SubscriberEntry {
  userId: number;
  enabled: boolean;
}

// Subscriber lists keyed by notification section id.
type SectionSubscribers = Record<string, SubscriberEntry[]>;

// Section-key-order-insensitive serialization for dirty-state comparison.
const serializeSubscribers = (s: SectionSubscribers) =>
  JSON.stringify(Object.keys(s).sort().map((k) => [k, s[k]]));

// ===================================================================
// Notification sections (static metadata; subscribers come from the API)
// ===================================================================
const NOTIFICATION_SECTIONS = [
  {
    id: "onboarding-commenced",
    title: "New Hire — Onboarding Commenced",
    description: "Sent when a new hire's onboarding journey is kicked off.",
  },
  {
    id: "it-provisioning",
    title: "New Hire — IT Provisioning",
    description: "Sent for IT Provisioning when a new hire's onboarding journey is kicked off.",
  },
  {
    id: "prestart-forms-complete",
    title: "New Hire — Pre-Start Forms Complete",
    description: "Sent when the new hire has completed all required pre-start forms.",
  },
  {
    id: "onboarding-complete",
    title: "Congratulations — Onboarding Complete!",
    description: "Sent when the new hire's full onboarding journey is marked as complete.",
  },
];

const AVATAR_COLORS = [
  "bg-blue-500",
  "bg-purple-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
];

// ===================================================================
// Icon helper
// ===================================================================
const CategoryIcon: React.FC<{ icon: RequirementCategory["icon"]; className?: string }> = ({ icon, className }) => {
  switch (icon) {
    case "monitor": return <Monitor className={className} />;
    case "smartphone": return <Smartphone className={className} />;
    case "shield": return <Shield className={className} />;
    case "wrench": return <Wrench className={className} />;
    default: return <FileText className={className} />;
  }
};

// ===================================================================
// Main Component
// ===================================================================
const OnboardingSettings: React.FC = () => {
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("email");
  const [users, setUsers] = useState<NotificationSubscriber[]>([]);
  const [sectionSubscribers, setSectionSubscribers] = useState<SectionSubscribers>({});
  // Last loaded/saved snapshot — compared against sectionSubscribers to
  // detect unsaved changes.
  const [savedSubscribers, setSavedSubscribers] = useState<SectionSubscribers>({});
  // Selected user id in each section's "add user" dropdown (keyed by section id)
  const [addUserSelection, setAddUserSelection] = useState<Record<string, string>>({});

  // Requirements state
  const [categories, setCategories] = useState<RequirementCategory[]>([]);
  const [showAddItem, setShowAddItem] = useState<string | null>(null); // categoryId
  const [newItemName, setNewItemName] = useState("");
  const [newItemDescription, setNewItemDescription] = useState("");
  const [newItemLead, setNewItemLead] = useState("IT");
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const [newCategoryIcon, setNewCategoryIcon] = useState<RequirementCategory["icon"]>("monitor");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showDeleteCategory, setShowDeleteCategory] = useState<string | null>(null);

  const canManage = ability.can(ACTIONS.UPDATE, RESOURCES.ONBOARDING_SETTINGS);

  const hasUnsavedChanges =
    serializeSubscribers(sectionSubscribers) !== serializeSubscribers(savedSubscribers);

  // -----------------------------------------------------------------
  // Fetch users for subscriber list + saved settings + requirement categories
  // -----------------------------------------------------------------
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchNotificationSettings(), fetchCategories()]);
      setLoading(false);
    };
    init();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get<{ success: boolean; users: any[] }>("/api/users?all_users=true");
      if (res.success && Array.isArray(res.users)) {
        const mapped = res.users.map((u: any) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          initials: u.name?.split(" ").map((n: string) => n[0]).join("").toUpperCase() || "",
          avatarColor: AVATAR_COLORS[u.id % AVATAR_COLORS.length],
        }));
        setUsers(mapped);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // -----------------------------------------------------------------
  // Fetch saved notification subscriber config
  // -----------------------------------------------------------------
  const fetchNotificationSettings = async () => {
    try {
      const res = await api.get<{ success: boolean; data: SectionSubscribers }>(
        "/api/onboarding/settings/notifications"
      );
      if (res.success && res.data) {
        setSectionSubscribers(res.data);
        setSavedSubscribers(res.data);
      }
    } catch (error) {
      console.error("Error fetching notification settings:", error);
    }
  };

  // -----------------------------------------------------------------
  // Fetch requirement categories from the API
  // -----------------------------------------------------------------
  const fetchCategories = async () => {
    try {
      const res = await api.get<{ success: boolean; data: any[] }>(
        "/api/onboarding/requirements/categories"
      );
      if (res.success && res.data) {
        const mapped = res.data.map(mapRequirementCategoryFromApi);
        setCategories(mapped);
        setExpandedCategories(new Set(mapped.map((c) => c.id)));
      }
    } catch (error) {
      console.error("Error fetching requirement categories:", error);
    }
  };

  // -----------------------------------------------------------------
  // Subscriber handlers (per section): tick/untick, add, remove
  // -----------------------------------------------------------------
  const toggleSubscriber = (sectionId: string, userId: number) => {
    setSectionSubscribers((prev) => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).map((entry) =>
        entry.userId === userId ? { ...entry, enabled: !entry.enabled } : entry
      ),
    }));
  };

  const addSubscriber = (sectionId: string) => {
    const userId = parseInt(addUserSelection[sectionId] || "", 10);
    if (!Number.isInteger(userId)) return;
    setSectionSubscribers((prev) => {
      const current = prev[sectionId] || [];
      if (current.some((entry) => entry.userId === userId)) return prev;
      return { ...prev, [sectionId]: [...current, { userId, enabled: true }] };
    });
    setAddUserSelection((prev) => ({ ...prev, [sectionId]: "" }));
  };

  const removeSubscriber = (sectionId: string, userId: number) => {
    setSectionSubscribers((prev) => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).filter((entry) => entry.userId !== userId),
    }));
  };

  // -----------------------------------------------------------------
  // Save settings
  // -----------------------------------------------------------------
  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await api.post<{ success: boolean; message: string }>(
        "/api/onboarding/settings/notifications",
        { notifications: sectionSubscribers }
      );
      if (res.success) {
        setSavedSubscribers(sectionSubscribers);
        toast.success("Notification settings saved");
      } else {
        toast.error(res.message || "Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------
  // Requirements handlers
  // -----------------------------------------------------------------
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const addItem = async (categoryId: string) => {
    if (!newItemName.trim()) return;
    try {
      const res = await api.post<{ success: boolean; data: any }>(
        `/api/onboarding/requirements/categories/${categoryId}/items`,
        {
          name: newItemName.trim(),
          description: newItemDescription.trim() || null,
          lead: newItemLead,
        }
      );
      if (res.success && res.data) {
        const newItem: RequirementItem = {
          id: String(res.data.id),
          name: res.data.name,
          description: res.data.description || "",
          lead: res.data.lead || "",
        };
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === categoryId ? { ...cat, items: [...cat.items, newItem] } : cat
          )
        );
        setNewItemName("");
        setNewItemDescription("");
        setNewItemLead("IT");
        setShowAddItem(null);
        toast.success("Requirement item added");
      }
    } catch (error) {
      console.error("Error adding requirement item:", error);
    }
  };

  const deleteItem = async (categoryId: string, itemId: string) => {
    try {
      const res = await api.delete<{ success: boolean }>(
        `/api/onboarding/requirements/items/${itemId}`
      );
      if (res.success) {
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === categoryId
              ? { ...cat, items: cat.items.filter((i) => i.id !== itemId) }
              : cat
          )
        );
        toast.success("Requirement item removed");
      }
    } catch (error) {
      console.error("Error deleting requirement item:", error);
    }
  };

  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await api.post<{ success: boolean; data: any }>(
        "/api/onboarding/requirements/categories",
        {
          name: newCategoryName.trim(),
          description: newCategoryDescription.trim() || null,
          icon: newCategoryIcon,
        }
      );
      if (res.success && res.data) {
        const newCategory = mapRequirementCategoryFromApi({ ...res.data, items: res.data.items || [] });
        setCategories((prev) => [...prev, newCategory]);
        setExpandedCategories((prev) => new Set(prev).add(newCategory.id));
        setNewCategoryName("");
        setNewCategoryDescription("");
        setNewCategoryIcon("monitor");
        setShowAddCategory(false);
        toast.success("Category added");
      }
    } catch (error) {
      console.error("Error adding category:", error);
    }
  };

  const deleteCategory = async (categoryId: string) => {
    try {
      const res = await api.delete<{ success: boolean }>(
        `/api/onboarding/requirements/categories/${categoryId}`
      );
      if (res.success) {
        setCategories((prev) => prev.filter((c) => c.id !== categoryId));
        setExpandedCategories((prev) => {
          const next = new Set(prev);
          next.delete(categoryId);
          return next;
        });
        setShowDeleteCategory(null);
        toast.success("Category removed");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  if (!canManage) {
    return <AccessRestricted />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  const TABS = [
    { id: "leads", label: "Leads" },
    { id: "requirements", label: "Requirements" },
    { id: "email", label: "Email Notifications" },
  ];

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10">
      {/* Breadcrumb + Back */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button
          onClick={() => navigate("/onboarding")}
          className="flex items-center gap-1 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Onboarding
        </button>
      </div>

      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Onboarding Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configure leads and requirement options for onboarding workflows
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== EMAIL NOTIFICATIONS TAB ===== */}
      {activeTab === "email" && (
        <div className="space-y-6">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-900">Email Notification Subscribers</p>
              <p className="text-sm text-blue-700 mt-0.5">
                Add users to each notification section, then tick who should receive the email. Unticked users will not be notified. Sections with no users added fall back to the default recipients (the hiring stakeholders or Administration users).
              </p>
            </div>
            {hasUnsavedChanges && (
              <span className="inline-flex items-center gap-1.5 shrink-0 text-xs font-medium text-red-700 bg-red-100 border border-red-200 px-2.5 py-1 rounded-full">
                Unsaved changes
              </span>
            )}
          </div>

          {/* Notification Sections */}
          <div className="space-y-6">
            {NOTIFICATION_SECTIONS.map((section) => {
              const entries = sectionSubscribers[section.id] || [];
              const addedIds = new Set(entries.map((entry) => entry.userId));
              const availableUsers = users.filter((u) => !addedIds.has(u.id));
              // Entries whose user no longer exists (or is inactive) are
              // hidden here and dropped from the payload on the next save.
              const rows = entries
                .map((entry) => ({ entry, user: users.find((u) => u.id === entry.userId) }))
                .filter((r): r is { entry: SubscriberEntry; user: NotificationSubscriber } => r.user !== undefined);

              return (
                <div key={section.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {/* Section Header */}
                  <div className="px-5 py-4 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
                  </div>

                  {/* Added Users */}
                  {rows.length === 0 ? (
                    <div className="px-5 py-6 text-center">
                      <p className="text-sm text-gray-400">No users added to this notification yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {rows.map(({ entry, user }) => (
                        <div
                          key={`${section.id}-${user.id}`}
                          className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50/50 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className={`w-8 h-8 shrink-0 rounded-full ${user.avatarColor} flex items-center justify-center text-white text-xs font-bold`}
                            >
                              {user.initials}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                              <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <input
                              type="checkbox"
                              checked={entry.enabled}
                              onChange={() => toggleSubscriber(section.id, user.id)}
                              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                              title={entry.enabled ? "Receives this email" : "Does not receive this email"}
                            />
                            <button
                              onClick={() => removeSubscriber(section.id, user.id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                              title="Remove from this notification"
                            >
                              <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add User */}
                  <div className="border-t border-gray-100 px-5 py-3 bg-gray-50/50 flex flex-col sm:flex-row gap-2">
                    <Combobox
                      options={availableUsers.map((user) => ({
                        label: user.name,
                        value: String(user.id),
                        subtitle: user.email,
                      }))}
                      value={addUserSelection[section.id] || undefined}
                      onChange={(val) =>
                        setAddUserSelection((prev) => ({ ...prev, [section.id]: val || "" }))
                      }
                      placeholder="Select a user to add…"
                      searchPlaceholder="Search users..."
                      emptyMessage={availableUsers.length === 0 ? "All users have been added" : "No user found."}
                      className="flex-1 bg-white"
                    />
                    <Button
                      size="sm"
                      onClick={() => addSubscriber(section.id)}
                      disabled={!addUserSelection[section.id]}
                      className="bg-gray-900 hover:bg-gray-800 text-white h-10"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || !hasUnsavedChanges}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      )}

      {/* ===== LEADS TAB (placeholder) ===== */}
      {activeTab === "leads" && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium text-gray-500">Leads</p>
          <p className="text-sm text-gray-400">Lead assignment settings will appear here</p>
        </div>
      )}

      {/* ===== REQUIREMENTS TAB ===== */}
      {activeTab === "requirements" && (
        <div className="space-y-6">
          {/* Info Banner */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-900">About Requirement Management</p>
              <p className="text-sm text-emerald-700 mt-0.5">
                Define and organize onboarding requirement options that requesters can choose from when creating new onboarding requests. Requirements are grouped by category and assigned to the appropriate lead for completion.
              </p>
            </div>
          </div>

          {/* Section Header */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Requirement Categories</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage the available requirements that can be selected during onboarding
            </p>
          </div>

          {/* Category Cards */}
          <div className="space-y-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                {/* Category Header */}
                <div className="flex items-center gap-4 px-5 py-4">
                  <div className={`w-10 h-10 rounded-lg ${category.iconBg} flex items-center justify-center`}>
                    <CategoryIcon icon={category.icon} className={`w-5 h-5 ${category.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">{category.name}</h3>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {category.items.length} {category.items.length === 1 ? "item" : "items"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{category.description}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setShowAddItem(category.id)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Add item"
                    >
                      <Plus className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => setShowDeleteCategory(category.id)}
                      className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                      title="Delete category"
                    >
                      <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                    </button>
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform ${
                          expandedCategories.has(category.id) ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Items List */}
                {expandedCategories.has(category.id) && (
                  <div className="border-t border-gray-100">
                    {category.items.length === 0 ? (
                      <div className="px-5 py-6 text-center">
                        <p className="text-sm text-gray-400">No items in this category</p>
                        <button
                          onClick={() => setShowAddItem(category.id)}
                          className="text-sm text-blue-600 hover:text-blue-700 mt-2"
                        >
                          Add your first item
                        </button>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-50">
                        {category.items.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                              <div>
                                <span className="text-sm font-medium text-gray-900">{item.name}</span>
                                {item.description && (
                                  <span className="text-sm text-gray-500 ml-2">{item.description}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                {item.lead}
                              </span>
                              <button
                                onClick={() => deleteItem(category.id, item.id)}
                                className="p-1.5 rounded hover:bg-red-50 transition-colors"
                                title="Remove item"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Item Inline Form */}
                    {showAddItem === category.id && (
                      <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/50">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <Input
                            placeholder="Item name"
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                            className="bg-white"
                          />
                          <Input
                            placeholder="Description (optional)"
                            value={newItemDescription}
                            onChange={(e) => setNewItemDescription(e.target.value)}
                            className="bg-white"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={newItemLead}
                              onChange={(e) => setNewItemLead(e.target.value)}
                              className="flex-1 h-10 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {LEAD_OPTIONS.map((lead) => (
                                <option key={lead} value={lead}>{lead}</option>
                              ))}
                            </select>
                            <Button
                              size="sm"
                              onClick={() => addItem(category.id)}
                              disabled={!newItemName.trim()}
                              className="bg-gray-900 hover:bg-gray-800 text-white"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add
                            </Button>
                            <button
                              onClick={() => {
                                setShowAddItem(null);
                                setNewItemName("");
                                setNewItemDescription("");
                                setNewItemLead("IT");
                              }}
                              className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <X className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Add Category Button */}
            <button
              onClick={() => setShowAddCategory(true)}
              className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50/50 transition-all"
            >
              <Plus className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-500">Add New Category</span>
            </button>
          </div>
        </div>
      )}

      {/* ===== ADD CATEGORY DIALOG ===== */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-gray-700">Category Name</label>
              <Input
                placeholder="e.g., Software Access"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <Input
                placeholder="e.g., Software and application access"
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Icon</label>
              <div className="flex items-center gap-2 mt-1">
                {(["monitor", "smartphone", "shield", "wrench"] as const).map((icon) => (
                  <button
                    key={icon}
                    onClick={() => setNewCategoryIcon(icon)}
                    className={`p-3 rounded-lg border transition-all ${
                      newCategoryIcon === icon
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <CategoryIcon
                      icon={icon}
                      className={`w-5 h-5 ${
                        newCategoryIcon === icon ? "text-blue-600" : "text-gray-500"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddCategory(false)}>
                Cancel
              </Button>
              <Button
                onClick={addCategory}
                disabled={!newCategoryName.trim()}
                className="bg-gray-900 hover:bg-gray-800 text-white"
              >
                Add Category
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== DELETE CATEGORY DIALOG ===== */}
      <Dialog open={!!showDeleteCategory} onOpenChange={() => setShowDeleteCategory(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mt-2">
            Are you sure you want to delete this category? All items within it will also be removed.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteCategory(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => showDeleteCategory && deleteCategory(showDeleteCategory)}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingSettings;
